import React from 'react';
import { Grid, TextField } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import AnonymousFreeTextBanner from '../pedigree-chart/AnonymousFreeTextBanner';
import axios from 'src/axios';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';

export default function FamilyNote({
  user,
  onDirtyChange,
  // How many fields hold an unsaved change: 1 or 0 (TGCLOUD-1902).
  onChangeCount,
  saveRef,
  discardRef,
  // TGCLOUD-1791 — called with the saved text so the caller can put it back into
  // app state. Without this the component only ever told ITSELF about a save, so
  // anything else rendering `familyNote` — now including the chart header and
  // the export — showed the pre-edit value until the family was reloaded.
  // ExternalFamilyIds was given the same callback for the same reported defect
  // (TGCLOUD-1666).
  onSaved
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);

  // Re-seeds when the family changes. This was memoised on `[]`, so the
  // textarea kept the first family's note when the prop changed underneath it —
  // harmless while the note arrived from exactly one place, and a live bug the
  // moment a save dispatches a fresh value back into app state, which `onSaved`
  // now does.
  const initialNote = React.useMemo(
    () => user?.familyNote || '',
    [user?.familyNote]
  );

  const [note, setNote] = React.useState(initialNote);
  // Changed = the note differs from the note as last saved (TGCLOUD-1902).
  const changes = useChangedFields({
    baseline: { familyNote: initialNote },
    values: { familyNote: note },
    fields: ['familyNote']
  });
  const { resetBaseline } = changes;
  React.useEffect(() => {
    onDirtyChange?.(changes.isChanged);
  }, [changes.isChanged]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    onChangeCount?.(changes.count);
  }, [changes.count]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow a family switch or an externally-updated note, but never clobber what
  // the user is typing: `initialNote` only changes when the persisted value does.
  const lastSeededRef = React.useRef(initialNote);
  React.useEffect(() => {
    if (lastSeededRef.current === initialNote) return;
    lastSeededRef.current = initialNote;
    setNote(initialNote);
    resetBaseline({ familyNote: initialNote });
  }, [initialNote]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setNote(e.target.value);
  };

  const onSave = async () => {
    await axios
      .put(APICONSTANTS.setFamilyNote.replace(':user_id', user._id), {
        familyNote: note
      })
      .then(({ data }) => {
        const saved = data.familyNote ?? '';
        lastSeededRef.current = saved;
        setNote(saved);
        resetBaseline({ familyNote: saved });
        onSaved?.(saved);
        setSnack({
          status: true,
          msg: t('Family note updated'),
          severity: 'success'
        });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('An error occurred'),
          severity: 'error'
        });
      });
  };

  const onDiscard = () => {
    setNote(changes.baseline.familyNote ?? initialNote);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <>
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <ChangedField
          component={Grid}
          size={12}
          name="familyNote"
          changed={changes.changed.familyNote}
        >
          <TextField
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            placeholder={t('Add a note about this family...')}
            value={note}
            onChange={handleChange}
          />
        </ChangedField>
      </Grid>
    </>
  );
}
